import { Button, Grid, InputAdornment, MenuItem, Select, TextField } from "@mui/material";
import { IFilterParams } from "ag-grid-community";
import { IFilterReactComp } from "ag-grid-react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

const PercentFilter = forwardRef<IFilterReactComp, IFilterParams>(
  ({ filterChangedCallback, valueGetter }, ref) => {
    const [filterActive, setFilterActive] = useState(false);
    const [trigger, setTrigger] = useState(false);
    const [type, setType] = useState<"greaterThanOrEqual" | "lessThanOrEqual" | "between">(
      "between"
    );
    const [from, setFrom] = useState<"" | number>("");
    const [fromError, setFromError] = useState({
      error: false,
      text: "",
    });
    const [to, setTo] = useState<"" | number>("");
    const [toError, setToError] = useState({
      error: false,
      text: "",
    });

    // expose AG Grid Filter Lifecycle callbacks
    useImperativeHandle(ref, () => {
      return {
        doesFilterPass(params) {
          const value = valueGetter(params.node);
          if (type === "greaterThanOrEqual") {
            return value !== "" && value >= from!;
          } else if (type === "lessThanOrEqual") {
            return value !== "" && value <= to!;
          } else {
            return value !== "" && value >= from! && value <= to!;
          }
        },

        isFilterActive() {
          return filterActive;
        },

        getModel() {
          if (!filterActive) return null;
          const savedStatus = {
            type,
            from,
            to,
          };

          if (type === "between") {
            return {
              type: "between",
              filter: `${from}|${to}`,
              savedStatus,
            };
          } else {
            return {
              type,
              filter: from,
              savedStatus,
            };
          }
        },

        setModel(model: any) {
          if (model?.savedStatus) {
            const { savedStatus } = model;
            setType(savedStatus.type ?? "between");
            setFrom(savedStatus.from ?? "");
            setTo(savedStatus.to ?? "");
            setFilterActive(true);
            setTrigger(true);
          } else {
            setFilterActive(false);
            setFrom("");
            setTo("");
            setTrigger(true);
          }
        },
      };
    });

    const onClear = () => {
      setFilterActive(false);
      setFrom("");
      setTo("");
      setFromError({
        error: false,
        text: "",
      });
      setToError({
        error: false,
        text: "",
      });
      setTrigger(true);
    };

    const onApply = () => {
      setFilterActive(true);
      setTrigger(true);
    };

    useEffect(() => {
      if (trigger) {
        filterChangedCallback();
        setTrigger(false);
      }
    }, [trigger, filterChangedCallback]);

    useEffect(() => {
      setFilterActive(() => {
        return from !== "" || to !== "";
      });
    }, [from, to]);

    const isInteger = (str: string) => {
      const num = parseInt(str);
      return !isNaN(num) && num.toString() === str;
    };

    const handleChange = (value: string, type: string) => {
      if (type === "from") {
        setFrom(value === "" ? "" : +value);
        if (isInteger(value) || value === "") {
          setFromError({
            error: false,
            text: "",
          });
        } else {
          setFromError({
            error: true,
            text: "Please input integer number",
          });
        }
      } else if (type === "to") {
        setTo(value === "" ? "" : +value);
        if (isInteger(value) || value === "") {
          setToError({
            error: false,
            text: "",
          });
        } else {
          setToError({
            error: true,
            text: "Please input integer number",
          });
        }
      }
    };

    return (
      <Grid
        container={true}
        direction="column"
        className="ag-filter-body-wrapper ag-simple-filter-body-wrapper"
      >
        <Grid
          container={true}
          direction="column"
          spacing={1}
        >
          <Grid item={true}>
            <Select
              size="small"
              fullWidth={true}
              variant="outlined"
              margin="dense"
              value={type}
              //@ts-ignore
              onChange={(e) => setType(e.target.value)}
              MenuProps={{
                disablePortal: true,
                keepMounted: true,
              }}
            >
              <MenuItem value="between">Between</MenuItem>
              <MenuItem value="greaterThanOrEqual">Greater Than or Equal</MenuItem>
              <MenuItem value="lessThanOrEqual">Less Than or Equal</MenuItem>
            </Select>
          </Grid>
          <Grid item={true}>
            <TextField
              fullWidth={true}
              type="number"
              placeholder="Please input number"
              variant="standard"
              value={from}
              error={fromError.error}
              helperText={fromError.text}
              onChange={(e) => {
                handleChange(e.target.value, "from");
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>
          {type === "between" ? (
            <Grid item={true}>
              <TextField
                fullWidth={true}
                type="number"
                placeholder="To..."
                variant="standard"
                value={to}
                error={toError.error}
                helperText={toError.text}
                onChange={(e) => {
                  handleChange(e.target.value, "to");
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
          ) : null}
        </Grid>
        <Grid
          item={true}
          container={true}
          justifyContent="space-evenly"
        >
          <Grid item={true}>
            <Button
              variant="text"
              disabled={!filterActive}
              onClick={onClear}
              className="ag-standard-button ag-filter-apply-panel-button"
            >
              Reset filter
            </Button>
          </Grid>
          <Grid item={true}>
            <Button
              variant="text"
              className="ag-standard-button ag-filter-apply-panel-button"
              onClick={onApply}
              disabled={
                from === "" ||
                fromError.error ||
                (type === "between" && (to === "" || toError.error))
              }
            >
              Apply filter
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

export default PercentFilter;
